/**
 * Single source of truth for every route destination shared across the app: the public
 * `/join/:slug` signup page and its in-store QR display companion, the canonical `/privacy`
 * and `/terms` legal documents, and the administrator's `/admin` platform and business scopes.
 * Every route definition, QR encode target, copy link, and preview URL is built from the
 * constants and functions here so callers can never drift onto a different or
 * runtime-configured destination.
 */

const JOIN_SEGMENT = "join";
const DISPLAY_SEGMENT = "qr";

/** React Router path for the canonical public privacy policy — a same-origin route, never a
 * runtime-configured external URL, so every retailer's signup page and the account-creation
 * flow always link to a document this app actually owns and serves. */
export const PRIVACY_ROUTE_PATH = "/privacy";

/** React Router path for the canonical public terms of service. See `PRIVACY_ROUTE_PATH`. */
export const TERMS_ROUTE_PATH = "/terms";

/** React Router path pattern for the public signup page. */
export const SIGNUP_ROUTE_PATH = `/${JOIN_SEGMENT}/:slug`;

/** React Router path pattern for the public in-store QR display page. */
export const DISPLAY_ROUTE_PATH = `/${JOIN_SEGMENT}/:slug/${DISPLAY_SEGMENT}`;

/** Path (no origin) to a retailer's public signup page. `slug` is percent-encoded so a slug
 * containing reserved URL characters can never escape its own path segment. */
export function buildSignupPath(slug: string): string {
  return `/${JOIN_SEGMENT}/${encodeURIComponent(slug)}`;
}

/** Path (no origin) to a retailer's public in-store QR display page. */
export function buildDisplayPath(slug: string): string {
  return `${buildSignupPath(slug)}/${DISPLAY_SEGMENT}`;
}

/** Absolute signup URL for `slug` against `origin` (defaults to the current page's origin) — the
 * exact destination the QR code on both the owner panel and the display page must encode. */
export function buildSignupUrl(slug: string, origin: string = window.location.origin): string {
  return `${origin}${buildSignupPath(slug)}`;
}

/** Absolute in-store display URL for `slug` against `origin` — opened by the owner's
 * "Open in-store display" action. Never the value encoded into the QR code itself. */
export function buildDisplayUrl(slug: string, origin: string = window.location.origin): string {
  return `${origin}${buildDisplayPath(slug)}`;
}

/* ---------------------------------------------------------------------------------------------
 * Administrator scopes
 *
 * The administrator operates in exactly two scopes, and the URL says which one at a glance:
 * `/admin/<section>` is the platform — every business at once — and
 * `/admin/businesses/<businessId>/<destination>` is one business. Both are real paths, so a deep
 * link, a refresh, and the browser's own Back button behave the way they do everywhere else in
 * the product. Scope selection is never carried in the query string.
 * ------------------------------------------------------------------------------------------ */

/** The administrator's platform destinations, in navigation order. */
export const ADMIN_PLATFORM_SECTIONS = ["businesses", "analytics", "members", "offers"] as const;

export type AdminPlatformSection = (typeof ADMIN_PLATFORM_SECTIONS)[number];

/** Every destination inside one administrator-selected business, in navigation order: the five
 * task destinations, then the governance destination that deliberately sits outside the task set. */
export const BUSINESS_DESTINATIONS = [
  "overview",
  "analytics",
  "customize",
  "members",
  "campaigns",
  "administration",
] as const;

export type BusinessDestination = (typeof BUSINESS_DESTINATIONS)[number];

/** Every React Router path pattern that resolves to the administrator workspace. `/admin` and a
 * business without a destination are incomplete rather than invalid: the workspace canonicalizes
 * them onto a real scope with a replacing navigation, so they never occupy a history entry. */
export const ADMIN_ROUTE_PATHS = [
  "/admin",
  "/admin/:section",
  "/admin/businesses/:businessId",
  "/admin/businesses/:businessId/:destination",
] as const;

/** Path to one of the administrator's platform destinations. */
export function buildAdminSectionPath(section: AdminPlatformSection): string {
  return `/admin/${section}`;
}

/** Path to one destination inside one business. `businessId` is percent-encoded so an identifier
 * containing reserved URL characters can never escape its own path segment. */
export function buildAdminBusinessPath(businessId: string, destination: BusinessDestination): string {
  return `/admin/businesses/${encodeURIComponent(businessId)}/${destination}`;
}

/** The administrator's canonical landing scope: the whole platform, read as its business directory. */
export const ADMIN_LANDING_PATH = buildAdminSectionPath("businesses");
